/** Left sidebar for the partner console — mirrors AppSidebar styling.
 *
 * Commercials is reseller-only (a reseller manages its own agents; a
 * commercial doesn't). Desktop sticky aside + mobile <details> collapse,
 * same as the customer app.
 */
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { usePartnerMe } from "@/lib/partner";

import { NavIcon } from "./navIcons";

// `roles` limits a reseller-team item to those partner_roles. Commercials
// (not a reseller) aren't role-gated — they keep their own scoped views.
type Item = {
  to: string;
  key: string;
  resellerOnly?: boolean;
  commercialOnly?: boolean;
  roles?: Array<"administrator" | "manager" | "billing">;
};

const NAV: Item[] = [
  { to: "/partner", key: "nav.partner.overview" },
  {
    to: "/partner/organizations",
    key: "nav.partner.organizations",
    roles: ["administrator", "manager"],
  },
  {
    to: "/partner/team",
    key: "nav.partner.team",
    resellerOnly: true,
    roles: ["administrator"],
  },
  // A commercial's own flat team (no roles). Shown only for commercials.
  {
    to: "/partner/commercial/team",
    key: "nav.partner.team",
    commercialOnly: true,
  },
  {
    to: "/partner/commercials",
    key: "nav.partner.commercials",
    resellerOnly: true,
    roles: ["administrator"],
  },
  {
    to: "/partner/commissions",
    key: "nav.partner.commissions",
    roles: ["administrator", "billing"],
  },
  {
    to: "/partner/payouts",
    key: "nav.partner.payouts",
    roles: ["administrator", "billing"],
  },
  {
    to: "/partner/settings",
    key: "nav.partner.settings",
    resellerOnly: true,
    roles: ["administrator"],
  },
];

function NavContent() {
  const { t } = useTranslation();
  const me = usePartnerMe();
  const isReseller = me.data?.kind === "reseller";
  const role = me.data?.partner_role ?? "";
  const visible = (i: Item) => {
    if (i.resellerOnly && !isReseller) return false;
    if (i.commercialOnly && isReseller) return false;
    if (!i.roles || !isReseller) return true; // commercials keep their views
    return i.roles.includes(role as "administrator" | "manager" | "billing");
  };
  return (
    <nav className="space-y-0.5">
      {NAV.filter(visible).map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end
          className={({ isActive }) =>
            "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition " +
            (isActive
              ? "bg-nb-mint/15 text-nb-mint"
              : "text-nb-text-2 hover:bg-nb-surface/40 hover:text-nb-text")
          }
        >
          <NavIcon name={i.key} />
          <span>{t(i.key)}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function PartnerSidebar() {
  const { t } = useTranslation();
  return (
    <>
      <aside className="hidden lg:block sticky top-24 self-start h-[calc(100vh-7rem)] overflow-y-auto pr-2 -mr-2">
        <NavContent />
      </aside>
      <details className="lg:hidden mb-4">
        <summary className="flex cursor-pointer items-center justify-between rounded-md border border-nb-border bg-nb-surface/30 px-4 py-2.5 text-sm text-nb-text">
          <span className="font-medium">{t("sidebar.menu")}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </summary>
        <div className="mt-3 rounded-md border border-nb-border bg-nb-surface/20 p-3">
          <NavContent />
        </div>
      </details>
    </>
  );
}
